'use client';

import { ChevronDown, Download, Plug } from 'lucide-react';
import { Button } from '../../atoms/button/button';
import {
	DetailField,
	DetailSelectInput,
	DetailTextInput,
} from '../../molecules/detail-fields/detail-fields';

export interface MeterReaderField {
	key: string;
	label: string;
	type: 'text' | 'password';
	placeholder?: string;
}

export interface MeterReaderViewProps {
	mode: string;
	options: { value: string; label: string }[];
	fields?: MeterReaderField[];
	config: Record<string, string>;
	loading: boolean;
	error?: string | null;
	busy: boolean;
	importing: boolean;
	canImport: boolean;
	gatewayEnabled: boolean;
	lastSyncAt?: string | null;
	lastError?: string | null;
	testMessage: { ok: boolean; message: string } | null;
	onModeChange: (mode: string) => void;
	onFieldChange: (key: string, value: string) => void;
	onSave: () => void;
	onTest: () => void;
	onImport: () => void;
	onOpenGateway: () => void;
	onOpenMqtt: () => void;
}

export function MeterReaderView(p: MeterReaderViewProps) {
	return (
		<details className="group rounded-xl border border-border bg-card p-4 sm:p-5">
			<summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-md text-sm font-semibold text-text focus-visible:outline-2 focus-visible:outline-accent [&::-webkit-details-marker]:hidden">
				Configurazione del lettore
				<ChevronDown
					className="size-4 shrink-0 transition-transform group-open:rotate-180"
					aria-hidden
				/>
			</summary>
			<div className="mt-4 space-y-4">
				{p.loading ? (
					<p className="text-sm text-text-muted">Caricamento configurazione…</p>
				) : p.error ? (
					<p role="alert" className="text-sm text-danger">
						{p.error}
					</p>
				) : (
					<>
						<DetailField label="Lettore">
							<DetailSelectInput
								value={p.mode}
								onChange={p.onModeChange}
								options={p.options}
								disabled={p.busy || p.importing}
							/>
						</DetailField>
						{p.mode === 'gateway' && (
							<div className="flex flex-wrap items-center justify-between gap-3 text-sm text-text-muted">
								<span>Gateway {p.gatewayEnabled ? 'configurato' : 'non configurato'}</span>
								<Button type="button" variant="outline" size="sm" onClick={p.onOpenGateway}>
									<Plug />
									{p.gatewayEnabled ? 'Gestisci gateway' : 'Configura gateway'}
								</Button>
							</div>
						)}
						{p.fields?.map((f) => (
							<DetailField key={f.key} label={f.label}>
								<DetailTextInput
									type={f.type}
									value={p.config[f.key] ?? ''}
									placeholder={f.placeholder}
									onChange={(v) => p.onFieldChange(f.key, v)}
									disabled={p.busy || p.importing}
								/>
							</DetailField>
						))}
						{p.testMessage && (
							<output
								className={p.testMessage.ok ? 'text-sm text-accent-green' : 'text-sm text-danger'}
							>
								{p.testMessage.message}
							</output>
						)}
						{p.fields && (
							<p className="text-xs text-text-muted">
								{p.lastSyncAt
									? `Ultima lettura: ${new Date(p.lastSyncAt).toLocaleString('it-IT')}`
									: 'Nessuna sincronizzazione'}
								{p.lastError && <span className="text-danger"> · {p.lastError}</span>}
							</p>
						)}
						<div className="flex flex-wrap justify-end gap-2">
							{p.canImport && (
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={p.onImport}
									disabled={p.busy || p.importing}
								>
									<Download />
									{p.importing ? 'Importazione…' : 'Importa storico'}
								</Button>
							)}
							{p.fields && (
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={p.onTest}
									disabled={p.busy || p.importing}
								>
									Verifica connessione
								</Button>
							)}
							<Button
								type="button"
								variant="accent"
								size="sm"
								onClick={p.onSave}
								disabled={p.busy || p.importing}
							>
								{p.busy ? 'Attendere…' : 'Salva lettore'}
							</Button>
						</div>
					</>
				)}
				<div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
					<p className="text-xs text-text-muted">
						Le connessioni MQTT sono condivise dalla configurazione CER.
					</p>
					<Button type="button" variant="outline" size="sm" onClick={p.onOpenMqtt}>
						Gestisci MQTT
					</Button>
				</div>
			</div>
		</details>
	);
}
